import { CcfBox, CcfTextField, CcfTypography } from '@nice-ccf/ui-controls';
import styles from './simulator-v2-journey-details-tab.module.scss';
import { useEffect } from 'react';
import { makeStyles } from '@mui/styles';
import { useDispatch, useSelector } from 'react-redux';
import {
  getSelectedChannel,
  getSelectedContact,
  getSelectedMedia,
  setSelectedChannel,
  setSelectedMedia,
} from '../../+state/simulatorV2.slice';
import { DropDown, generateDate, generateRandomId } from '../../utils/utils';
import { contact } from '../../+state/contact';
import { useAgentName } from '../../utils/useAgentName';
import {
  contactButtons,
  VoiceAuthenticationStatus,
  VoiceContactStatus,
} from '@nice-ccf/core-sdk';
import { customCopilotEmailTemplate } from 'libs/react/ui-components/src/lib/ccf-app-space/ccf-copilot/ccf-copilot-chat/mock-data/copilot-email-custom-flow';
import { customCopilotVoiceTemplate } from 'libs/react/ui-components/src/lib/ccf-app-space/ccf-copilot/ccf-copilot-chat/mock-data/copilot-voice-custom-flow';
import { customCopilotTemplate } from 'libs/react/ui-components/src/lib/ccf-app-space/ccf-copilot/ccf-copilot-chat/mock-data/custom-flow';

/* eslint-disable-next-line */
export interface SimulatorV2JourneyDetailsTabProps {
  setUpdatedContact: any;
  updatedContact: any;
  isEditClick?: boolean;
}

const useStyles = makeStyles({
  textFieldStyle: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '4px',
      height: '36px',
      border: '1px solid #AFC1CE',
    },
  },
});

const selectInputStyle = {
  borderRadius: '4px',
  border: '1px solid #c4c4c4',
  height: '36px',
};

const selectRootStyle = {
  padding: '10px',
  fontSize: '1rem',
};

/**
 * used to show the Journey Details Tab
 * @param props - SimulatorV2JourneyDetailsTabProps
 * @example ` <SimulatorV2JourneyDetailsTab />`
 */
export function SimulatorV2JourneyDetailsTab(
  props: SimulatorV2JourneyDetailsTabProps
) {
  const { setUpdatedContact, updatedContact, isEditClick } = props;
  const agentName = useAgentName();

  const classes = useStyles();
  const mediaOptions = ['Digital', 'Voice', 'Voicemail', 'VideoCall'];
  const channelOptions = [
    'Email',
    'Instagram',
    'X',
    'Facebook',
    'Linkedin',
    'Chat',
  ];

  const updatedMedia = useSelector(getSelectedMedia);
  const updatedChannel = useSelector(getSelectedChannel);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!isEditClick) {
      setUpdatedContact((prev: any) => ({
        ...prev,
        id: generateRandomId(),
        media: updatedMedia,
        channel: updatedChannel,
        journeyName: prev.journeyName || '',
        journeyDescription: prev.journeyDescription || '',
        lastUpdatedDate: generateDate(),
        createdDate: generateDate(),
      }));
    }
  }, []);

  useEffect(() => {
    if (!isEditClick && agentName) {
      setUpdatedContact((prev: any) => ({
        ...prev,
        createdBy: agentName,
      }));
    }
  }, [agentName]);

  /**
   * function used to handle input change
   * @example - handleInputChange()
   */
  const handleInputChange = (field: string, value: any) => {
    setUpdatedContact((prevState: any) => ({
      ...prevState,
      [field]: value,
      lastUpdatedDate: generateDate(),
    }));

    if (field === 'media' || field === 'channel') {
      const media = field === 'media' ? value : updatedMedia;
      const channel = field === 'channel' ? value : updatedChannel;

      const selected = contact.find((item) =>
        media === 'Digital' ? item.channel === channel : item.media === media
      );
      // Reset fields to avoid stale data
      // Reset only the required fields
      const currentUpdatedContact = {
        mediaType: undefined,
        messageType: undefined,
        queueName: undefined,
        contactButtons: undefined,
      };

      const updateCopilotData = {
        autoSummary: media === 'Voice' ? 'On' : selected?.autoSummary,
        contactStatus: ['Voice', 'Voicemail', 'VideoCall'].includes(media)
          ? VoiceContactStatus.RINGING
          : 'new',
        rtaStatus: VoiceAuthenticationStatus.AUTHENTICATED,
        autoSummaryText:
          media === 'Voice'
            ? customCopilotVoiceTemplate?.finalAutoSummary
            : selected?.autoSummaryText,
        perviousInteractionJsonData:
          media === 'Digital' && channel === 'Email'
            ? customCopilotEmailTemplate?.journeyHistory
            : ['voice', 'voicemail', 'videocall'].includes(
                media.toLowerCase()
              ) ||
              ['x', 'instagram', 'facebook', 'linkedin'].includes(
                channel.toLowerCase()
              )
            ? customCopilotVoiceTemplate?.journeyHistory
            : media === 'Digital' && channel === 'Chat'
            ? customCopilotTemplate?.journeyHistory
            : '',
        rtSummary:
          media === 'Digital' && channel === 'Email'
            ? customCopilotEmailTemplate?.rtSummary
            : media === 'Voice'
            ? customCopilotVoiceTemplate?.rtSummary
            : media === 'Digital' && channel === 'Chat'
            ? customCopilotTemplate?.rtSummary
            : '',
        transferSummary:
          media === 'Digital' && channel === 'Email'
            ? customCopilotEmailTemplate?.agentTransferSummary
            : media === 'Voice'
            ? customCopilotVoiceTemplate?.agentTransferSummary
            : media === 'Digital' && channel === 'Chat'
            ? customCopilotTemplate?.agentTransferSummary
            : '',

        customerChatReplyJsonData: [],
        botTranscriptData:
          media === 'Digital' &&
          channel === 'Chat' &&
          customCopilotTemplate?.botTranscriptData,
        botTranscript: selected?.botTranscript
          ? selected?.botTranscript
          : false,
      };
      // perviousInteractionJsonData: customCopilotTemplate?.journeyHistory,
      //       rtSummary: customCopilotTemplate?.rtSummary,
      //       showtransferSummary: false,
      //       transferSummary: customCopilotTemplate?.agentTransferSummary,
      //       botTranscriptData: customCopilotTemplate?.botTranscriptData,

      if (media === 'Voice') {
        Object.assign(currentUpdatedContact, {
          mediaType: 'Voice',
          messageType: '253-895-8956',
          queueName: 'Cust. Service Request',
          contactButtons: [
            contactButtons.hold,
            contactButtons.mask,
            contactButtons.mute,
            contactButtons.keypad,
            contactButtons.transfer,
            contactButtons.record,
            contactButtons.hungup,
          ],
        });
      } else if (media === 'Digital') {
        if (field === 'channel' && value === 'Email') {
          Object.assign(currentUpdatedContact, {
            mediaType: 'Digital',
            messageType: 'Post',
            queueName: 'Mention',
            contactButtons: [contactButtons.transfer, contactButtons.commit],
          });
        } else if (
          field === 'channel' &&
          value === 'Chat' &&
          updatedContact?.copilotAssist === 'Yes'
        ) {
          Object.assign(currentUpdatedContact, {
            mediaType: 'Digital',
            messageType: 'Chat',
            queueName: 'VIP Client',
            contactButtons: [contactButtons.transfer, contactButtons.commit],
          });
        } else {
          Object.assign(currentUpdatedContact, {
            mediaType: 'Digital',
            messageType: 'Post',
            queueName: 'Mention',
            contactButtons: [contactButtons.transfer, contactButtons.commit],
          });
        }
      } else if (media === 'VideoCall') {
        Object.assign(currentUpdatedContact, {
          mediaType: 'VideoCall',
          messageType: 'VideoCall',
          queueName: 'Damaged Luggage',
          contactButtons: [
            contactButtons.hold,
            contactButtons.mask,
            contactButtons.mute,
            contactButtons.keypad,
            contactButtons.transfer,
            contactButtons.record,
            contactButtons.hungup,
          ],
        });
      } else if (media === 'Voicemail') {
        Object.assign(currentUpdatedContact, {
          messageType: '253-895-8976',
          queueName: 'Cust. Service Request',
          contactButtons: [
            contactButtons.outBoundCall,
            contactButtons.transfer,
            contactButtons.commitment,
          ],
        });
      }

      if (selected) {
        const obj = {
          ...(isEditClick ? updatedContact : selected),
          ...updateCopilotData,
          ...currentUpdatedContact,
          media,
          channel: media === 'VideoCall' ? 'VideoCall' : channel,
          journeyName: updatedContact.journeyName || '',
          journeyDescription: updatedContact.journeyDescription || '',
          createdBy: updatedContact.createdBy || agentName,
          lastUpdatedDate: generateDate(),
          createdDate: updatedContact.createdDate || generateDate(),
          id: isEditClick ? updatedContact.id : generateRandomId(),
        };

        setUpdatedContact(obj);
      }

      if (field === 'media') {
        dispatch(setSelectedMedia(value));
      } else {
        dispatch(setSelectedChannel(value));
      }
    }
  };

  return (
    <div className={styles['container']}>
      <CcfBox style={{ padding: '20px', paddingBottom: '0px' }}>
        <CcfTypography className={styles.headerText}>
          Journey Details
        </CcfTypography>
        <CcfBox style={{ margin: '15px 0px 0px' }}>
          <CcfBox className={styles.containerGrid}>
            <CcfTypography className={styles.inputLabel}>
              Journey Name <span style={{ color: '#C2041A' }}>*</span>
            </CcfTypography>
            <CcfTextField
              id="journeyName"
              variant="outlined"
              className={classes.textFieldStyle}
              size="small"
              value={updatedContact.journeyName}
              focused={false}
              onChange={(e) => handleInputChange('journeyName', e.target.value)}
            />
          </CcfBox>
          <CcfBox className={styles.containerGrid}>
            <CcfTypography className={styles.inputLabel}>
              Journey Description
            </CcfTypography>
            <textarea
              id="journeyDescription"
              className={`${classes.textFieldStyle} ${styles.textAreaStyle}`}
              rows={4}
              style={{ resize: 'none' }}
              value={updatedContact.journeyDescription}
              onChange={(e) =>
                handleInputChange('journeyDescription', e.target.value)
              }
            />
          </CcfBox>
          <CcfBox className={styles.containerGrid}>
            <CcfTypography className={styles.inputLabel}>MEDIA</CcfTypography>
            <DropDown
              {...{
                currentValue: updatedContact.media,
                field: 'media',
                label: '',
                options: mediaOptions,
                handleInputChange,
                id: `sm_customerDetails_${updatedContact.media}`,
                stackName: 'customerDetails_media',
                selectInputStyle,
                selectRootStyle,
              }}
            />
          </CcfBox>
          {(updatedContact.media === 'Digital' ||
            updatedMedia === 'Digital') && (
            <CcfBox className={styles.containerGrid}>
              <CcfTypography className={styles.inputLabel}>
                CHANNEL
              </CcfTypography>
              <DropDown
                {...{
                  currentValue: updatedContact.channel,
                  field: 'channel',
                  label: '',
                  options: channelOptions,
                  handleInputChange,
                  id: `sm_contactDetails_channel_${updatedContact.channel}`,
                  stackName: 'contactDetails_channel',
                  selectInputStyle,
                  selectRootStyle,
                }}
              />
            </CcfBox>
          )}
        </CcfBox>
      </CcfBox>
    </div>
  );
}

export default SimulatorV2JourneyDetailsTab;
